import { Injectable } from '@angular/core';
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { environment } from '@environment';
import { IUserStorageModel } from '@features/auth/models';
import { LocalStorageService } from '@shared/services';
import { EXPIRE_HOURS } from '@features/auth/constants';
import { parseBooleanAttribute } from '@features/auth-amplify/utils';

interface IExpiryState {
  isValid: boolean;
  allowWithoutExpiry: boolean;
  shouldClearStorage: boolean;
}

@Injectable({
  providedIn: 'root',
})
// All of the functionality in this service can be accomplished by using `aws-amplify` package
// But that package is quite big so we want to omit loading it on start
// Instead we limit using of that library to lazy loaded modules
// And for app start functionality (getting of userData, tokens etc.) we use this service
// Logic here is added based on `aws-amplify` internal code
export class CognitoUserService {
  private cognitoUser: CognitoUser;
  private userPool: CognitoUserPool;
  private readonly EXPIRY_KEY_SUFFIX = '.expiresAt';
  private readonly LEGACY_ORPHAN_EXPIRY_KEYS = ['null.expiresAt', 'undefined.expiresAt'];
  // Amplify v6 stores these keys without a client-scoped prefix.
  // They must be cleared on signOut to prevent stale state causing auth failures on refresh.
  private readonly AMPLIFY_GLOBAL_KEYS = ['amplify-signin-with-hostedUI'];
  private readonly MILLISECONDS_PER_HOUR = 3600000;
  private readonly FALLBACK_EXPIRE_HOURS = 5;

  public get initializedCognitoUser(): CognitoUser {
    if (!this.cognitoUser) {
      this.initCognitoProps();
    }

    return this.cognitoUser;
  }

  public get initializedUserPool(): CognitoUserPool {
    if (!this.userPool) {
      this.initCognitoProps();
    }

    return this.userPool;
  }

  constructor(private localStorageService: LocalStorageService) {}

  public getStorageUserData(): IUserStorageModel | null {
    if (!this.checkExpiry()) {
      return null;
    }

    const cognitoUserStoragePrefix = this.getUserStoragePrefix();
    if (!cognitoUserStoragePrefix) {
      return null;
    }

    const userDataFromStorage = this.localStorageService.getItem(`${cognitoUserStoragePrefix}.userData`, true);

    return this.mapUserData(userDataFromStorage?.UserAttributes);
  }

  public getIdToken(): Observable<string | null> {
    if (!this.checkExpiry()) {
      return of(null);
    }

    return this.requestCurrentSession()
      .pipe(map((session) => session.getIdToken().getJwtToken()))
      .pipe(
        catchError(() => of(null)),
        take(1),
        map((idToken) => idToken ?? this.getStorageTokens()?.id ?? null)
      );
  }

  public signOut(): void {
    try {
      this.cognitoUser?.signOut();
    } catch (error) {
      console.warn('[CognitoUserService] Failed to sign out Cognito user, continuing cleanup:', error);
    } finally {
      this.clearCognitoProps();
    }

    // Clean up all Cognito-related storage (including signInDetails, tokens, and expiry)
    this.clearAllCognitoStorage();
  }

  private clearAllCognitoStorage(): void {
    try {
      const cognitoPrefix = `${this.getUserStorageCognitoPrefix()}.`;

      // Remove all keys for THIS app's Cognito client (scoped by userPoolWebClientId)
      // to avoid affecting other Cognito apps on the same domain.
      const allKeys = this.localStorageService.getAllKeys();
      const keysToRemove = allKeys.filter((key) => key.startsWith(cognitoPrefix));

      keysToRemove.forEach((key) => {
        this.localStorageService.removeItem(key);
      });

      // Also clear Amplify's global keys that aren't client-scoped.
      // These can cause auth failures if left behind when Amplify's signOut fails.
      this.AMPLIFY_GLOBAL_KEYS.forEach((key) => {
        this.localStorageService.removeItem(key);
      });
    } catch (error) {
      console.warn('[CognitoUserService] Failed to clear Cognito storage:', error);
      // Don't throw - degraded signout is better than no signout.
      // Worst case: stale tokens remain, but user can try logging in again.
    }
  }

  public initCognitoProps(): void {
    try {
      const currentUsername = this.getUserStorageUsername();
      if (currentUsername) {
        this.userPool = new CognitoUserPool({
          UserPoolId: environment.awsAmplifyConfig.userPoolId,
          ClientId: environment.awsAmplifyConfig.userPoolWebClientId,
        });
        this.cognitoUser = new CognitoUser({
          Username: currentUsername,
          Pool: this.userPool,
        });
      }
    } catch (error) {
      console.warn('[CognitoUserService] Failed to initialize Cognito props:', error);
      this.clearCognitoProps();
    }
  }

  public updateExpiry(): void {
    // Validate EXPIRE_HOURS to prevent misconfiguration
    const hours = this.getValidatedExpiryHours();

    const userStoragePrefix = this.getUserStoragePrefix();
    if (!userStoragePrefix) {
      return;
    }

    const expiryStorageName = `${userStoragePrefix}${this.EXPIRY_KEY_SUFFIX}`;
    const expiryTimestamp = Date.now() + hours * this.MILLISECONDS_PER_HOUR;
    this.localStorageService.setItem(expiryStorageName, expiryTimestamp);
  }

  /**
   * Validates and returns the expiry hours configuration.
   * Falls back to a safe default if configuration is invalid.
   */
  private getValidatedExpiryHours(): number {
    if (!Number.isFinite(EXPIRE_HOURS) || EXPIRE_HOURS <= 0) {
      console.warn(
        `[CognitoUserService] Invalid EXPIRE_HOURS: ${EXPIRE_HOURS}, using default ${this.FALLBACK_EXPIRE_HOURS} hours`
      );
      return this.FALLBACK_EXPIRE_HOURS;
    }
    return EXPIRE_HOURS;
  }

  public checkExpiry(): boolean {
    return this.performExpiryCheck();
  }

  /**
   * Checks if legacy user storage context exists AND performs cleanup of orphan keys.
   * Use this in mutation-allowed paths (e.g., checkExpiry/performExpiryCheck).
   */
  private hasLegacyUserStorageContextWithCleanup(): boolean {
    const currentUsername = this.getUserStorageUsername();
    if (!currentUsername) {
      this.clearLegacyOrphanExpiryKeys();
      return false;
    }

    return true;
  }

  /**
   * Read-only check for legacy user storage context. No side effects.
   * Use this when classification is needed without mutation.
   */
  private hasLegacyUserStorageContextReadOnly(): boolean {
    const username = this.getUserStorageUsernameReadOnly();
    return !!username;
  }

  /**
   * Returns the failure reason to use when Amplify has no active session.
   * This method is side-effect-free: it does not mutate storage or update sliding-window expiry.
   */
  public determineLegacyRehydrateFailureReason(): 'expired' | 'no_session' {
    if (!this.hasLegacyUserStorageContextReadOnly()) {
      return 'no_session';
    }

    const expiryStorageName = this.getExpiryStorageNameReadOnly();
    if (!expiryStorageName) {
      return 'no_session';
    }

    const expiryState = this.getExpiryState(expiryStorageName);
    return expiryState.isValid ? 'no_session' : 'expired';
  }

  /**
   * Performs expiry check with sliding window behavior.
   *
   * SLIDING WINDOW EXPIRY:
   * This implementation uses a sliding expiry window (default: 5 hours via EXPIRE_HOURS).
   * Each successful check extends the session by another EXPIRE_HOURS from now.
   * This means active users never get logged out due to inactivity during a session,
   * but if they close the browser and return after EXPIRE_HOURS, they must re-login.
   *
   * Alternative approaches considered:
   * - Absolute expiry (tied to Cognito token exp): Would require users to re-login
   *   every time the Cognito token expires, even during active sessions.
   * - No expiry: Security risk - stale tokens remain indefinitely.
   */
  private performExpiryCheck(): boolean {
    if (!this.hasLegacyUserStorageContextWithCleanup()) {
      return false;
    }

    const expiryStorageName = this.getExpiryStorageName();
    if (!expiryStorageName) {
      return false;
    }

    const expiryState = this.getExpiryState(expiryStorageName);

    if (expiryState.shouldClearStorage) {
      this.clearAllCognitoStorage();
      return false;
    }

    if (expiryState.isValid) {
      // Only update sliding window expiry for non-rememberMe sessions
      // (rememberMe sessions have allowWithoutExpiry: true and no sliding window)
      if (!expiryState.allowWithoutExpiry) {
        this.updateExpiry();
      }
      return true;
    }

    return false;
  }

  /**
   * Determines the state of the expiry value in storage.
   */
  private getExpiryState(expiryStorageName: string): IExpiryState {
    const rawExpiry = this.localStorageService.getItem(expiryStorageName);

    // Validate Cognito session tokens first (required for all session types)
    if (!this.hasValidStoredSession()) {
      return { isValid: false, allowWithoutExpiry: false, shouldClearStorage: true };
    }

    // Missing expiry indicates no sliding expiry configured (e.g., rememberMe: true sessions)
    // Still valid because Cognito tokens passed validation above
    if (rawExpiry === null || rawExpiry === undefined) {
      return { isValid: true, allowWithoutExpiry: true, shouldClearStorage: false };
    }

    const expiryTimestamp = Number(rawExpiry);

    // Malformed expiry value - treat as security issue and force fresh login
    if (Number.isNaN(expiryTimestamp)) {
      return { isValid: false, allowWithoutExpiry: false, shouldClearStorage: true };
    }

    // Check if sliding window expired
    if (expiryTimestamp <= Date.now()) {
      return { isValid: false, allowWithoutExpiry: false, shouldClearStorage: true };
    }

    // Still valid (both Cognito tokens and sliding window)
    return { isValid: true, allowWithoutExpiry: false, shouldClearStorage: false };
  }

  private hasValidStoredSession(): boolean {
    try {
      const session = this.getUserSession();
      if (!session) {
        return false;
      }

      return session.isValid();
    } catch (error) {
      console.warn('[CognitoUserService][SECURITY] Session validation failed - treating as invalid:', error);
      return false;
    }
  }

  private getExpiryStorageName(): string | null {
    const prefix = this.getUserStoragePrefix();
    return prefix ? `${prefix}${this.EXPIRY_KEY_SUFFIX}` : null;
  }

  /** Read-only version - no side effects */
  private getExpiryStorageNameReadOnly(): string | null {
    const prefix = this.getUserStoragePrefixReadOnly();
    return prefix ? `${prefix}${this.EXPIRY_KEY_SUFFIX}` : null;
  }

  private getUserStorageCognitoPrefix(): string {
    return `CognitoIdentityServiceProvider.${environment.awsAmplifyConfig.userPoolWebClientId}`;
  }

  /**
   * Returns the stored username, removing malformed values as a side effect.
   * Use getUserStorageUsernameReadOnly() when mutation is not allowed.
   */
  private getUserStorageUsername(): string | null {
    const lastUserStorageKey = `${this.getUserStorageCognitoPrefix()}.LastAuthUser`;
    const username = this.localStorageService.getItem(lastUserStorageKey);

    // Legacy clients could persist malformed values that break prefix construction.
    if (username === 'null' || username === 'undefined' || username === '') {
      this.localStorageService.removeItem(lastUserStorageKey);
      return null;
    }

    return username;
  }

  /**
   * Read-only version of getUserStorageUsername. Returns null for malformed values
   * but does NOT remove them from storage.
   */
  private getUserStorageUsernameReadOnly(): string | null {
    const lastUserStorageKey = `${this.getUserStorageCognitoPrefix()}.LastAuthUser`;
    const username = this.localStorageService.getItem(lastUserStorageKey);

    // Treat malformed values as missing without removing them.
    if (!username || username === 'null' || username === 'undefined') {
      return null;
    }

    return username;
  }

  private clearLegacyOrphanExpiryKeys(): void {
    this.LEGACY_ORPHAN_EXPIRY_KEYS.forEach((key) => {
      this.localStorageService.removeItem(key);
    });
  }

  private getUserStoragePrefix(): string | null {
    const currentUsername = this.getUserStorageUsername();
    return currentUsername ? `${this.getUserStorageCognitoPrefix()}.${currentUsername}` : null;
  }

  /** Read-only version - no side effects */
  private getUserStoragePrefixReadOnly(): string | null {
    const currentUsername = this.getUserStorageUsernameReadOnly();
    return currentUsername ? `${this.getUserStorageCognitoPrefix()}.${currentUsername}` : null;
  }

  private clearCognitoProps(): void {
    this.cognitoUser = null;
    this.userPool = null;
  }

  private requestCurrentSession(): Observable<CognitoUserSession> {
    return new Observable<CognitoUserSession>((subscriber) => {
      try {
        const currentSession = this.getUserSession();

        if (!currentSession) {
          subscriber.error(new Error('No current session available'));
          return;
        }

        if (currentSession?.isValid()) {
          subscriber.next(currentSession);
          subscriber.complete();
          return;
        }

        if (!this.initializedCognitoUser) {
          subscriber.error(new Error('Cognito user not initialized'));
          return;
        }

        this.initializedCognitoUser.getSession((err, val) => {
          if (err) {
            subscriber.error(new Error(`Failed to get session: ${err.message || 'Unknown error'}`));
          } else {
            subscriber.next(val);
            subscriber.complete();
          }
        });
      } catch (error) {
        subscriber.error(new Error(`Session request failed: ${(error as Error).message || 'Unknown error'}`));
      }
    });
  }

  private mapUserData(userAttributes): IUserStorageModel | null {
    if (!userAttributes) {
      return null;
    }

    const convertedUserAttributes = userAttributes.reduce((acc, item) => {
      acc[item.Name] = item.Value;

      return acc;
    }, {});

    return {
      id: convertedUserAttributes.sub,
      email: convertedUserAttributes.email,
      isEmailVerified: parseBooleanAttribute(convertedUserAttributes.email_verified),
    };
  }

  private getUserSession(): CognitoUserSession | null {
    const tokens = this.getStorageTokens();

    if (!tokens) {
      return null;
    }

    // Validate that essential tokens (id and access) exist before creating session
    // Refresh token can be null for some session types, but id and access are required
    if (!tokens.id || !tokens.access) {
      return null;
    }

    const idToken = new CognitoIdToken({ IdToken: tokens.id });
    const accessToken = new CognitoAccessToken({ AccessToken: tokens.access });
    const refreshTokenValue = typeof tokens.refresh === 'string' ? tokens.refresh : '';
    const refreshToken = new CognitoRefreshToken({ RefreshToken: refreshTokenValue });
    const clockDrift = parseInt(tokens.clockDrift) || 0;

    const sessionData = {
      IdToken: idToken,
      AccessToken: accessToken,
      RefreshToken: refreshToken,
      ClockDrift: clockDrift,
    };
    return new CognitoUserSession(sessionData);
  }

  private getStorageTokens(): {
    id: string | null;
    access: string | null;
    refresh: string | null;
    clockDrift: string | null;
  } | null {
    const cognitoUserStoragePrefix = this.getUserStoragePrefix();
    if (!cognitoUserStoragePrefix) {
      return null;
    }

    return {
      id: this.localStorageService.getItem(`${cognitoUserStoragePrefix}.idToken`),
      access: this.localStorageService.getItem(`${cognitoUserStoragePrefix}.accessToken`),
      refresh: this.localStorageService.getItem(`${cognitoUserStoragePrefix}.refreshToken`),
      clockDrift: this.localStorageService.getItem(`${cognitoUserStoragePrefix}.clockDrift`),
    };
  }
}
